import React, { useState } from 'react';
import { Image, Upload, Type } from 'lucide-react';

const QuestionCreation = ({ onAddQuestion, questionsCount, maxLength, maxQuestions }) => {
  const [questionType, setQuestionType] = useState('text');
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [imagePreview, setImagePreview] = useState('');
  const [imageText, setImageText] = useState(''); // New state for optional image text

  const handleSubmit = () => {
    if (!currentQuestion) return;
    
    const questionData = {
      type: questionType,
      content: currentQuestion
    };

    // Add optional text for image questions
    if (questionType === 'image' && imageText.trim()) {
      questionData.text = imageText.trim();
    }
    
    onAddQuestion(questionData);

    // Reset form
    setCurrentQuestion('');
    setImagePreview('');
    setImageText(''); // Reset image text
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        alert('Image must be less than 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setCurrentQuestion(reader.result); // Store base64 as question content
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="space-y-4 p-4 bg-white rounded-lg shadow">
      <div className="flex space-x-4 mb-4">
        <button
          onClick={() => {
            setQuestionType('text');
            setCurrentQuestion('');
            setImagePreview('');
            setImageText('');
          }}
          className={`flex items-center justify-center px-6 py-3 rounded-lg transition-colors ${
            questionType === 'text' 
              ? 'bg-blue-500 text-white shadow-md' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <Type className="w-5 h-5" />
          <span className="font-medium ml-2">Text</span>
        </button>
        <button
          onClick={() => {
            setQuestionType('image');
            setCurrentQuestion('');
            setImagePreview('');
            setImageText('');
          }}
          className={`flex items-center justify-center px-6 py-3 rounded-lg transition-colors ${
            questionType === 'image' 
              ? 'bg-blue-500 text-white shadow-md' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <Image className="w-5 h-5" />
          <span className="font-medium ml-2">Image</span>
        </button>
      </div>

      {questionType === 'text' ? (
        <div className="space-y-2">
        <input
          type="text"
          value={currentQuestion}
          onChange={(e) => setCurrentQuestion(e.target.value)}
          placeholder="Enter your question"
            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          maxLength={maxLength}
        />
          <div className="text-sm text-gray-500 text-right">
            {currentQuestion.length}/{maxLength} characters
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="space-y-2">
            <input
              type="text"
              value={imageText}
              onChange={(e) => setImageText(e.target.value)}
              placeholder="Optional: Add text description for the image"
              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              maxLength={200}
            />
            <div className="text-sm text-gray-500 text-right">
              {imageText.length}/200 characters
            </div>
          </div>
          
          <label className="block p-8 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleImageUpload}
            />
            <div className="flex flex-col items-center">
              <Upload className="w-8 h-8 mb-2 text-gray-400" />
              <p className="text-sm text-gray-500">Click to upload an image</p>
              <p className="text-xs text-gray-400 mt-1">Maximum size: 5MB</p>
            </div>
          </label>
          
          {imagePreview && (
            <div className="relative w-full max-w-2xl mx-auto">
              {imageText && (
                <p className="text-lg font-medium text-center mb-4 p-2 bg-gray-50 rounded">
                  {imageText}
                </p>
              )}
              <img
                src={imagePreview}
                alt="Question"
                className="w-full h-auto object-contain max-h-96 rounded-lg"
              />
              <button
                onClick={() => {
                  setImagePreview('');
                  setCurrentQuestion('');
                  setImageText(''); // Also reset image text
                }}
                className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center bg-red-500 text-white rounded-full shadow-lg hover:bg-red-600 transition-colors"
              >
                ×
              </button>
            </div>
          )}
        </div>
      )}

      <button
      onClick={handleSubmit}
        disabled={!currentQuestion || questionsCount >= maxQuestions}
        className={`w-full mt-4 px-4 py-3 rounded-lg font-medium transition-colors ${
          !currentQuestion || questionsCount >= maxQuestions
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : 'bg-blue-500 text-white hover:bg-blue-600'
        }`}
      >
        Add Question ({questionsCount}/20)
      </button>
    </div>
  );
};

export default QuestionCreation;